import { useAlert } from '@ads/components-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useChangePolo } from '@/features/student-management/hooks/useChangePolo';
import { useEnrollmentList } from '@/features/student-management/hooks/useEnrollmentList';
import type { EnrollmentModalData } from '@/features/student-management/types/enrollment';
import { ENROLLMENT_CODE_TO_STATUS } from '@/features/student-management/types/enrollmentStatusCode';
import { EnrollmentFilterBar } from '@/features/student-management/ui/molecules/EnrollmentFilterBar';
import { CancelEnrollmentModal } from '@/features/student-management/ui/organisms/CancelEnrollmentModal';
import { ChangePoloModal } from '@/features/student-management/ui/organisms/ChangePoloModal';
import { EnrollmentTable } from '@/features/student-management/ui/organisms/EnrollmentTable';
import { EnrollmentListTemplate } from '@/features/student-management/ui/templates/EnrollmentListTemplate';

export const Route = createFileRoute('/_authenticated/enrollments/')({
  component: EnrollmentListPage,
});

function EnrollmentListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('personManagement');
  const { alert } = useAlert();
  const [pageIndex, setPageIndex] = useState(0);
  const { parents, isLoading, totalParents, totalCount, totalPages, pageSize, stats, hasSearched } =
    useEnrollmentList(pageIndex + 1);

  useEffect(() => {
    setPageIndex(0);
  }, []);

  const [cancelEnrollment, setCancelEnrollment] = useState<EnrollmentModalData | null>(null);
  const [poloEnrollment, setPoloEnrollment] = useState<EnrollmentModalData | null>(null);
  const { changePolo, isChanging } = useChangePolo(() => setPoloEnrollment(null));

  return (
    <EnrollmentListTemplate
      filterPanel={<EnrollmentFilterBar isLoading={isLoading} />}
      {...(hasSearched ? { stats } : {})}
      onNewEnrollment={() =>
        alert({ alertType: 'warning', title: t('enrollmentList.newEnrollmentNotImplemented') })
      }
      onBatchStatusChange={() => navigate({ to: '/enrollments/batch-status' })}
    >
      <EnrollmentTable
        parents={parents}
        isLoading={isLoading}
        hasSearched={hasSearched}
        totalParents={totalParents}
        totalCount={totalCount}
        totalPages={totalPages}
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPageChange={setPageIndex}
        onManageEnrollment={id =>
          navigate({
            to: '/enrollments/$enrollmentId',
            params: { enrollmentId: String(id) },
          })
        }
        onCancelEnrollment={(child, parent) =>
          setCancelEnrollment({
            id: child.courseRegistrationId ?? 0,
            personName: child.studentName ?? '',
            personCpf: child.cpf ?? '',
            personEmail: child.email ?? '',
            status:
              (child.status && ENROLLMENT_CODE_TO_STATUS[child.status]) || 'payment_selection',
            courseName: parent.courseName ?? '',
            className: parent.classStudentDescription ?? '',
            poleName: parent.poloName ?? null,
            enrollmentDate: child.date ?? '',
          })
        }
        onChangePolo={(child, parent) =>
          setPoloEnrollment({
            id: child.courseRegistrationId ?? 0,
            personName: child.studentName ?? '',
            personCpf: child.cpf ?? '',
            personEmail: child.email ?? '',
            status:
              (child.status && ENROLLMENT_CODE_TO_STATUS[child.status]) || 'payment_selection',
            courseName: parent.courseName ?? '',
            className: parent.classStudentDescription ?? '',
            poleName: parent.poloName ?? null,
            enrollmentDate: child.date ?? '',
          })
        }
        onEditPerson={id => navigate({ to: '/persons/$id', params: { id: String(id) } })}
        onBatchStatusChange={() => navigate({ to: '/enrollments/batch-status' })}
      />

      <CancelEnrollmentModal
        enrollment={cancelEnrollment}
        open={cancelEnrollment !== null}
        onClose={() => setCancelEnrollment(null)}
        onConfirm={_data => setCancelEnrollment(null)}
      />

      <ChangePoloModal
        enrollment={poloEnrollment}
        open={poloEnrollment !== null}
        onClose={() => setPoloEnrollment(null)}
        onConfirm={changePolo}
        isLoading={isChanging}
      />
    </EnrollmentListTemplate>
  );
}
